import React from 'react';
import { buildInterleavedStripeMap, calculateStripeAllocations } from '../utils/interleavedRng';

/**
 * RNGChart component displays RNG pool rewards with TRUE INTERLEAVED distribution
 * Shows the random value rolled and rewards with evenly spaced stripes across spectrum
 * NEW: Uses true interleaving where each item's stripes are evenly distributed
 */
export default function RNGChart({ 
  randomValue, 
  rewards, 
  selectedReward,
  totalChance = 100,
  style = {} 
}) {
  if (!rewards || rewards.length === 0) {
    return null;
  }

  // Calculate stripe allocations for TRUE interleaved distribution (SAME AS BACKEND)
  const NUM_STRIPES = 100;
  
  // Assign colors to each reward - bright, vibrant colors
  const colors = [
    '#ff4444', // Bright red
    '#44ff44', // Bright green  
    '#ffff44', // Bright yellow
    '#44ffff', // Bright cyan
    '#ff44ff', // Bright magenta
    '#ff8844', // Bright orange
    '#4444ff'  // Bright blue
  ];
  
  // Use true interleaved stripe calculation (matches backend)
  const stripeAllocations = calculateStripeAllocations(rewards, 'chance');
  const stripeOwners = buildInterleavedStripeMap(stripeAllocations);
  
  // Add color and stripe info to rewards
  const positionedRewards = rewards.map((reward, index) => {
    const chance = Number(reward.chance) || 0;
    const normalizedChance = totalChance > 0 ? chance / totalChance : 0;
    
    return {
      ...reward,
      color: colors[index % colors.length],
      normalizedChance: normalizedChance * 100,
      stripeCount: stripeAllocations[index]
    };
  });

  // Find which reward was selected based on the selectedReward prop
  const selectedRewardIndex = selectedReward ? 
    positionedRewards.findIndex(reward => {
      // Handle both recipe format (name/collection) and deals format (item_title/dcl_collection_address)
      const rewardName = reward.name || reward.item_title;
      const rewardCollection = (reward.collection || reward.dcl_collection_address || '').toLowerCase();
      const selectedName = selectedReward.name || selectedReward.item_title || selectedReward.collection_title;
      const selectedCollection = (selectedReward.collection || selectedReward.dcl_collection_address || '').toLowerCase();
      
      // Match by name AND collection (if collection exists)
      const nameMatch = rewardName === selectedName;
      const collectionMatch = !rewardCollection || !selectedCollection || rewardCollection === selectedCollection;
      
      return nameMatch && collectionMatch;
    }) : -1;

  return (
    <div style={{
      width: '100%',
      maxWidth: '720px',
      margin: '1rem auto',
      padding: '1rem',
      backgroundColor: 'rgba(138, 43, 226, 0.1)',
      border: '1px solid rgba(138, 43, 226, 0.3)',
      borderRadius: '12px',
      ...style
    }}>
      {/* Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '1.5rem'
      }}>
        <h3 style={{
          fontSize: '1.2rem',
          fontWeight: 'bold',
          color: '#8a2be2',
          margin: '0 0 0.5rem 0'
        }}>
          🎲 YOU ROLLED: {randomValue.toFixed(6)}
        </h3>
        <p style={{
          fontSize: '0.9rem',
          color: '#ccc',
          margin: '0 0 0.25rem 0'
        }}>
          Interleaved RNG Pool Results
        </p>
        <p style={{
          fontSize: '0.75rem',
          color: '#888',
          margin: 0,
          fontStyle: 'italic'
        }}>
          Items evenly spaced across 100 stripes with true interleaving
        </p>
      </div>

      {/* Line segment visualization - TRUE 100-stripe interleaved pattern */}
      <div style={{
        position: 'relative',
        height: '80px',
        marginBottom: '1rem',
        display: 'flex',
        alignItems: 'center'
      }}>
        {/* Draw actual 100-stripe interleaved pattern */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '0px',
          right: '0px',
          height: '24px',
          transform: 'translateY(-50%)',
          display: 'flex',
          borderRadius: '8px',
          overflow: 'hidden',
          border: '2px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
        }}>
          {stripeOwners.map((ownerIndex, stripeIndex) => (
            <div
              key={stripeIndex}
              style={{
                flex: '1',
                background: `linear-gradient(to bottom, ${positionedRewards[ownerIndex].color}, ${positionedRewards[ownerIndex].color}CC)`,
                opacity: stripeIndex === Math.floor(randomValue * NUM_STRIPES) ? 1 : 0.85,
                transition: 'opacity 0.2s',
                borderRight: stripeIndex < NUM_STRIPES - 1 ? '1px solid rgba(0, 0, 0, 0.2)' : 'none'
              }}
              title={`Stripe ${stripeIndex}: ${positionedRewards[ownerIndex].name}`}
            />
          ))}
        </div>

        {/* Random value indicator - arrow only at exact stripe position */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: `${(Math.floor(randomValue * NUM_STRIPES) / NUM_STRIPES * 100) + (1 / NUM_STRIPES * 100 / 2)}%`,
          transform: 'translate(-50%, -50%)',
          zIndex: 10
        }}>
          {/* Roll value label - above arrow */}
          <div style={{
            position: 'absolute',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#fff',
            color: '#000',
            padding: '3px 8px',
            borderRadius: '4px',
            fontSize: '0.75rem',
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)'
          }}>
            {randomValue.toFixed(4)}
          </div>
          
          {/* Arrow pointing down */}
          <div style={{
            position: 'absolute',
            top: '-12px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '10px solid transparent',
            borderRight: '10px solid transparent',
            borderTop: '12px solid #fff',
            filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5))'
          }} />
        </div>
      </div>

      {/* Reward details */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: rewards.length <= 2 ? 'repeat(2, 1fr)' : 
                            rewards.length === 3 ? 'repeat(3, 1fr)' :
                            'repeat(2, 1fr)',
        gap: '0.75rem',
        marginTop: '1rem'
      }}>
        {positionedRewards.map((reward, index) => (
          <div
            key={index}
            style={{
              padding: '0.75rem',
              backgroundColor: index === selectedRewardIndex 
                ? 'rgba(34, 197, 94, 0.2)' 
                : 'rgba(0, 0, 0, 0.3)',
              border: `3px solid ${index === selectedRewardIndex ? '#22c55e' : reward.color}`,
              borderRadius: '8px',
              textAlign: 'center',
              position: 'relative',
              boxShadow: index === selectedRewardIndex 
                ? `0 0 20px ${reward.color}80` 
                : `0 2px 8px ${reward.color}40`
            }}
          >
            {/* Selected indicator */}
            {index === selectedRewardIndex && (
              <div style={{
                position: 'absolute',
                top: '-12px',
                right: '-12px',
                width: '36px',
                height: '36px',
                backgroundColor: '#22c55e',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.2rem',
                color: '#fff',
                fontWeight: 'bold',
                boxShadow: '0 2px 8px rgba(34, 197, 94, 0.6)'
              }}>
                ✓
              </div>
            )}

            {/* Item image */}
            {reward.image && (
              <img
                src={reward.image}
                alt={reward.name || `Reward ${index + 1}`}
                style={{
                  width: '40px',
                  height: '40px',
                  objectFit: 'cover',
                  borderRadius: '6px',
                  marginBottom: '0.5rem',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}
              />
            )}

            {/* Color indicator dot */}
            <div style={{
              width: '12px',
              height: '12px',
              backgroundColor: reward.color,
              borderRadius: '50%',
              margin: '0 auto 0.5rem',
              border: '2px solid #fff',
              boxShadow: `0 0 8px ${reward.color}`
            }} />

            {/* Item name */}
            <div style={{
              fontSize: '0.8rem',
              fontWeight: 'bold',
              color: '#fff',
              marginBottom: '0.25rem',
              lineHeight: '1.2'
            }}>
              {reward.name || `Reward ${index + 1}`}
            </div>

            {/* Percentage chance */}
            <div style={{
              fontSize: '0.75rem',
              color: reward.color,
              fontWeight: 'bold',
              marginBottom: '0.25rem',
              textShadow: `0 0 10px ${reward.color}80`
            }}>
              {reward.normalizedChance.toFixed(1)}%
            </div>

            {/* Interleaved stripe count */}
            <div style={{
              fontSize: '0.7rem',
              color: '#ccc'
            }}>
              🎲 {reward.stripeCount} / 100 stripes
            </div>
            
            {/* Interleaved explanation on hover */}
            <div style={{
              fontSize: '0.65rem',
              color: '#888',
              marginTop: '0.25rem',
              fontStyle: 'italic'
            }}>
              Evenly spaced distribution
            </div>
          </div>
        ))}
      </div>

      {/* Summary and Explanation */}
      <div style={{
        marginTop: '1rem',
        padding: '0.75rem',
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        borderRadius: '6px',
        textAlign: 'center'
      }}>
        <div style={{
          fontSize: '0.9rem',
          color: '#ccc',
          marginBottom: '0.5rem'
        }}>
          {selectedRewardIndex >= 0 ? (
            <>
              <strong style={{ color: '#22c55e' }}>You won:</strong> {positionedRewards[selectedRewardIndex].name || `Reward ${selectedRewardIndex + 1}`}
            </>
          ) : (
            <span style={{ color: '#ff6b6b' }}>No reward selected</span>
          )}
        </div>
        
        {/* Interleaved explanation */}
        <div style={{
          marginTop: '0.75rem',
          paddingTop: '0.75rem',
          borderTop: '1px solid rgba(138, 43, 226, 0.3)',
          fontSize: '0.7rem',
          color: '#888',
          lineHeight: '1.4'
        }}>
          <strong style={{ color: '#8a2be2' }}>How True Interleaved RNG Works:</strong><br />
          Instead of contiguous ranges (0.00-0.{positionedRewards[0]?.normalizedChance.toFixed(0)}), 
          each item's stripes are evenly spaced across the entire 100-stripe spectrum. 
          This maintains exact {positionedRewards.map(r => r.normalizedChance.toFixed(0) + '%').join('/')} odds 
          with fair distribution and reduced streaks! 🎲
        </div>
      </div>
    </div>
  );
}
